import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, openBlock as _openBlock, createBlock as _createBlock, KeepAlive as _KeepAlive, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-145b3b28"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "tab-content" }
const _hoisted_3 = { key: 1 }

import { computed, ref, onMounted, provide, watch, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import ReportPermissionDialog from "../components/report-permission-dialog.vue";
import ReportPermissionDialogModel from "../components/report-permission-dialog-model";
import localStorageService from "../services/utilities/local-storage-service";
import { TeamApiModel, TeamSettingsApiModel, UserTeamPermissionsApiModel } from "../api-models";
import { csContext } from "../services/cs-context";
import HeaderWithTabs from "../components/home/header-with-tabs.vue";
import TeamDashboard from "./team/team-dashboard.vue";
import TeamLeadArea from "./team/team-lead-area.vue";
import MyDashboard from "./my-journey/my-dashboard.vue";
import MyDevelopment from "./my-journey/my-development.vue";
import { settleAll } from "../services/utilities/settle-all";
import LoadingPage from "./loading-page.vue";

export type HomeView = (typeof homeViews)[number];

const HOMEVIEW_KEY = "HomeView";


export default /*#__PURE__*/_defineComponent({
  __name: 'home-page',
  setup(__props) {

const homeViews = ["TeamDashboard", "TeamLeadArea", "MyDashboard", "MyDevelopment"] as const;

const isLoading = ref(true);

// Injected data
const currentTeam = ref<TeamApiModel | null>(csContext.currentTeam);
provide("currentTeam", currentTeam);

const teamUserPermissions = ref<UserTeamPermissionsApiModel>();
provide("teamUserPermissions", teamUserPermissions);

const teamSettingsModel = ref<TeamSettingsApiModel>();
provide("teamSettingsModel", teamSettingsModel);

const userIsTeamLeader = computed(() => !!teamUserPermissions.value?.isLeader);
provide("userIsTeamLeader", userIsTeamLeader);

const teamModel = ref<TeamApiModel>();
provide("teamModel", teamModel);

const isTeamGoalsSet = ref<boolean>();
provide("isTeamGoalsSet", isTeamGoalsSet);

// selected tab
const viewQueryParam = new URLSearchParams(window.location.search).get("view") as HomeView;

const isValidViewQueryParam = (view: string): view is HomeView => {
  return homeViews.includes(view as HomeView);
};

const currentTab = ref<HomeView>(
  viewQueryParam && isValidViewQueryParam(viewQueryParam)
    ? viewQueryParam
    : (localStorageService.get(HOMEVIEW_KEY) as HomeView) ?? "MyDevelopment",
);

const updateSelectedView = (view: HomeView) => {
  currentTab.value = view;
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);
  window.history.pushState({}, "", url.toString());
};

watch(
  currentTab,
  () => {
    localStorageService.save(HOMEVIEW_KEY, currentTab.value ?? "");
  },
  { immediate: true },
);
const components = {
  TeamDashboard,
  TeamLeadArea,
  MyDashboard,
  MyDevelopment,
};

const componentProps = ref({
  TeamDashboard: {
    id: currentTeam.value?.id,
    isTeamLeader: userIsTeamLeader,
    teamSettingsModel,
    teamModel,
  },
});

const selectedProps = computed(() => componentProps.value[currentTab.value] || {});

const selectedView = computed(() => components[currentTab.value]);

const reportPermissionDialogModel = ref<ReportPermissionDialogModel | null>(null);

const shouldShowReportSharingDialog = computed(
  () => !!reportPermissionDialogModel.value?.ids?.length,
);

onMounted(async () => {
  if (!currentTeam.value) {
    isLoading.value = false;
    return;
  }

  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, 50);

  const promises: Promise<void>[] = [];

  await axios
    .get(`/api/TeamSettings/${currentTeam.value?.id}`)
    .then((resp) => {
      const data = resp.data as TeamSettingsApiModel;
      teamSettingsModel.value = data;
      return data;
    })
    .catch((e: AxiosError) => {
      if (e.response?.status === 403) {
        window.location.href = "/ErrorPages/NotAuthorised";
      } else if (e.response?.status === 404) {
        window.location.href = "/ErrorPages/NotFound";
      } else {
        window.location.href = "/ErrorPages/Error";
      }
    });

  promises.push(
    axios.get(`/api/teamuserpermissions/${currentTeam.value?.id}`).then((resp) => {
      teamUserPermissions.value = resp.data as UserTeamPermissionsApiModel;
    }),
  );

  promises.push(
    axios.get(`/api/teams/${currentTeam.value?.id}`).then((resp) => {
      teamModel.value = resp.data as TeamApiModel;
    }),
  );

  promises.push(
    axios.get("/api/ReportPermissions/").then((resp) => {
      reportPermissionDialogModel.value = resp.data as ReportPermissionDialogModel;
    }),
  );
  promises.push(
    axios.get<boolean>(`/api/teamgoals/isTeamGoalsSet/${currentTeam.value?.id}`).then((resp) => {
      isTeamGoalsSet.value = resp.data;
    }),
  );

  await settleAll(promises);
  isLoading.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", {
    style: {"padding-top":"80px"},
    class: _normalizeClass(["team-area", { 'green-area': currentTab.value === 'TeamLeadArea' || currentTab.value === 'MyDevelopment' }])
  }, [
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(HeaderWithTabs, {
            "current-tab": currentTab.value,
            "onUpdate:selectedView": updateSelectedView
          }, null, 8, ["current-tab"]),
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(), _createBlock(_KeepAlive, null, [
              (_openBlock(), _createBlock(_resolveDynamicComponent(selectedView.value), _normalizeProps(_guardReactiveProps(selectedProps.value)), null, 16))
            ], 1024))
          ]),
          (shouldShowReportSharingDialog.value)
            ? (_openBlock(), _createBlock(ReportPermissionDialog, {
                key: 0,
                "data-test": "report-permissions-dialog",
                "refresh-page-on-change": true
              }))
            : _createCommentVNode("", true)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(LoadingPage)
        ]))
  ], 2))
}
}

})